import React from "react";
import { useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { Trash, UserPlus } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePeopleRepository } from "@/pages/people/hooks/use-people-repository";
import { PerformanceCastNestedType } from "@/pages/performances/schemas/performance.edit.form";

type PersonOption = {
  id: string;
  name: string;
  knownForDepartment?: string;
  media?: { photos?: Array<{ url?: string; blurhash?: string }> };
};

/**
 * Cast editor for a performance. Linked people ONLY: `PerformanceCastDbDto.PersonId` is
 * required and the command writes the DTO straight through, so a name-only credit is not
 * representable here the way it is on a play. The empty state says so rather than leaving a
 * curator looking for a button that cannot exist.
 */
export function PerformanceCastField({ id, inputProps }: AutoFormFieldProps) {
  const { search } = usePeopleRepository();
  const value: PerformanceCastNestedType[] = useWatch({ name: id }) ?? [];

  const setValue = (next: PerformanceCastNestedType[]) => {
    inputProps.onChange({
      target: { name: id, value: next },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const patch = (index: number, partial: Partial<PerformanceCastNestedType>) =>
    setValue(value.map((c, i) => (i === index ? { ...c, ...partial } : c)));

  const addLinked = (person: PersonOption | undefined) => {
    if (!person?.id) return;
    if (value.some((c) => c.personId === person.id)) return;
    setValue([
      ...value,
      {
        personId: person.id,
        name: person.name,
        role: null,
        department: person.knownForDepartment || "Acting",
        // `PerformanceCastDbDto` is written straight through, so a member that never
        // gets an `order` lands as `order: null` on save (see `updatePerformance`'s
        // "omit the key when the entry never had it" comment) — assign the next
        // index so a freshly added row does not.
        order: value.length,
        photo: person.media?.photos?.[0]?.url
          ? {
              url: person.media.photos[0].url!,
              blurhash: person.media.photos[0].blurhash ?? "",
            }
          : null,
      },
    ]);
  };

  return (
    <div className="flex w-full flex-col space-y-3 text-foreground">
      <span className="text-xs text-muted-foreground">
        Only people that exist in Mood can appear on a performance. A name-only
        credit lives on the play instead.
      </span>

      <RemoteSelect<PersonOption>
        className="w-[500px]"
        triggerType="change"
        onValueChanged={addLinked}
        accessorFn={(option) => ({
          id: option?.id || "",
          name: option?.name ?? "",
          iconUrl: option?.media?.photos?.[0]?.url,
          number: option?.knownForDepartment,
        })}
        onSearch={async (query, onStatusChanged) => {
          onStatusChanged("loading");
          const result = (await search(query)) as PersonOption[];
          onStatusChanged("success");
          return result;
        }}
      />

      <div className="flex flex-col gap-2">
        {value.map((item, index) => (
          <div
            key={item.personId}
            className="flex w-full flex-row items-center gap-2"
          >
            {item.photo?.url ? (
              <PhotoWithBlurhash
                className="size-10 shrink-0 rounded-full object-cover"
                url={item.photo.url}
                blurhash={item.photo.blurhash ?? ""}
              />
            ) : (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <UserPlus className="size-4" />
              </div>
            )}
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {item.name}
            </span>
            <div className="w-40 shrink-0">
              <Input
                className="h-8"
                placeholder="Role / character"
                defaultValue={item.role ?? ""}
                onBlur={(e) => patch(index, { role: e.target.value || null })}
              />
            </div>
            <div className="w-32 shrink-0">
              <Input
                className="h-8"
                placeholder="Department"
                defaultValue={item.department ?? "Acting"}
                onBlur={(e) =>
                  patch(index, { department: e.target.value || "Acting" })
                }
              />
            </div>
            <Button
              variant="secondary"
              size="icon"
              type="button"
              aria-label="Remove cast member"
              onClick={() => setValue(value.filter((_, i) => i !== index))}
            >
              <Trash className="size-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
